import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import imageSrc from '../../utils/imageData';
import NewAssessmentModal from '../../components/NewAssessmentModal';
import AssessmentResultsModal from '../../components/AssessmentResultsModal';
import DropdownAI from '../../components/DropdownAI';
import axios from "axios";
import { Config } from "../../config";
import { Radar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import GaugeChart from 'react-gauge-chart'
import LoadingSpinner from '../../components/LoadingSpinner';
import AIChat from '../../components/AIChat';
import AIVision from '../../components/AIVision';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Add this constant at the top of the file with the allowed MIME types
const ALLOWED_FILE_TYPES = [
  // Document formats
  'application/pdf',                                                    // .pdf
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword',                                                // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel',                                         // .xls
  'text/csv',                                                         // .csv
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint',                                    // .ppt
  'text/plain',                                                       // .txt
  
  // Image formats
  'image/jpeg',                                                       // .jpg, .jpeg
  'image/png',                                                        // .png
  'image/gif',                                                        // .gif
  'image/svg+xml',                                                    // .svg
  'image/webp',                                                       // .webp
  'image/bmp',                                                        // .bmp
  'image/tiff'                                                        // .tiff, .tif
];

function EngagementsTableItem(props) {

  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [controls, setControls] = useState([]);
  const [compliance, setCompliance] = useState({ data: {}, meta: {} });
  const [startDate, setStartDate] = useState('2025-03-21');
  const [finishDate, setFinishDate] = useState('2025-03-27');
  const [progress, setProgress] = useState(0);
  const [genScore, setGenScore] = useState(0);
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [showFinishMessage, setShowFinishMessage] = useState(false);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showPresentMessage, setShowPresentMessage] = useState(false);
  const [status, setStatus] = useState(props.status)
  const [restoreEnabled, setRestoreEnabled] = useState(false);
  const restoreFlag = useRef(false);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const saveFlag = useRef(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(true);
  const [editingCounters, setEditingCounters] = useState({});
  const [countermeasureTexts, setCountermeasureTexts] = useState({});
  const initialTextRef = useRef({});
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedCounter, setSelectedCounter] = useState(null);
  const [AICounter, setAICounter] = useState([]);
  const [displayedLines, setDisplayedLines] = useState([]);
  const [currentLine, setCurrentLine] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [genMessage, setGenMessage] = useState('Generando');
  const [generatingRow, setGeneratingRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState(null);
  const [dropdownSelections, setDropdownSelections] = useState({});
  const [showReEvalModal, setShowReEvalModal] = useState(false);
  const [showClarificationModal, setShowClarificationModal] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);
  const [clarificationComment, setClarificationComment] = useState('');
  const [clarificationStatuses, setClarificationStatuses] = useState({});
  // Add this state for the success modal
  const [showFinalizationMessage, setShowFinalizationMessage] = useState(false);
  // Add these state variables near the top with the other state declarations
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success'); // or 'error'
  const [activeAnalysisTabs, setActiveAnalysisTabs] = useState({});

  const animationIntervalRef = useRef(null);
  const resultsTableRef = useRef(null);
  
  const complianceOptions = [
    { id: 0, label: 'Pendiente', value: 0 },
    { id: 1, label: 'No Cumple', value: 0 },
    { id: 2, label: 'Cumple Parcialmente', value: 0.5 },
    { id: 3, label: 'Cumple', value: 1 },
    { id: 4, label: 'No Aplica', value: null }
  ];

  // Every per-framework rule below switches on the framework's database id,
  // and every one of them used to answer "nothing" for an id it did not list
  // — so a catalogue loaded after this file was written arrived dead: no
  // assessable rows, no radar, no shading. The explicit cases are the
  // frameworks that were hand-tuned and are left exactly as they were; the
  // default now reads the catalogue instead of giving up.
  //
  // A catalogue loaded by scripts/load_framework_catalog.py carries one row
  // per control, and the only aggregate rows are those with a purely numeric
  // ref. That is the loader's own validation rule and the one the Ops gap
  // tooling counts by, so it is the rule used here rather than a new one.
  const isGenericControl = (ref) => !/^\d+$/.test((ref || '').toString().trim());

  // The domains of the loaded catalogue, in the order they first appear —
  // the axes of the radar, and the keys its scores are stored under.
  const genericDomains = useMemo(() => {
    const seen = [];
    (controls || []).forEach((control) => {
      if (control.domain && !seen.includes(control.domain)) seen.push(control.domain);
    });
    return seen;
  }, [controls]);

  const isAssessable = (control) => { 
    switch (props.framework.id) { 
      case 1: return control.ref.length > 5;
      case 2:
      case 4: 
      case 6:
      case 7:
      case 8: 
      case 9:
        return control.ref.indexOf('.') > -1;
      default: return isGenericControl(control.ref);
    }
  }

  const radarLabels = (frameworkId) => {
    switch (frameworkId) {
      case 1: return ['GOBERNAR', 'IDENTIFICAR', 'PROTEGER', 'DETECTAR', 'RESPONDER', 'RECUPERAR'];
      case 2: return ['Controles Organizacionales', 'Controles de las Personas', 'Controles Físicos', 'Controles Tecnológicos'];
      case 4: return ['Inventario y Control de Activos Empresariales', 'Inventario y control de activos de software', 'Protección de datos', 'Configuración segura de los activos y el software de la empresa', 
        'Gestión de cuentas', 'Gestión del control de acceso', 'Gestión continua de vulnerabilidades', 'Gestión de registros de auditoría', 'Protecciones para el correo electrónico y el navegador web', 
        'Defensas contra el malware', 'Recuperación de datos', 'Gestión de la infraestructura de red', 'Supervisión y defensa de la red', 'Concienciación y formación en materia de seguridad', 'Gestión de proveedores de servicios', 
        'Seguridad del software de aplicación', 'Gestión de la respuesta a incidentes', 'Pruebas de Intrusión'];
      case 6: return ['Disposiciones Generales', 'Gestión de riesgos de TIC	', 'Gestión, clasificación e informes de incidentes relacionados con las TIC', 'Prueba de resiliencia operacional digital', 
        'Gestión del riesgo de terceros de las TIC', 'Acuerdos de intercambio de información'];
      case 7: return ['Marco de Trabajo y Responsabilidades', 'Administración Integral de Riesgos Tecnológicos, Seguridad Cibernética y de la Información', 'Gestión y Control de la Seguridad Cibernética y de la Información',
        'Operaciones de los Sistemas de Información y Continuidad del Negocio', 'Seguridad de las Comunicaciones', 'Desarrollo y Mantenimiento de los Sistemas de Información', 'Gestión y Control de Vulnerabilidades', 'Órganos de Gestión', 
        'Estructura Gerencial', 'Monitoreo Interno'];
      case 8: return ['MARCO DE TRABAJO Y ESTRUCTURA DE LA GOBERNANZA', 'GESTIÓN DEL RIESGO TECNOLÓGICO', 'MARCO DE CONTROL', 'MONITOREO Y EVALUACIÓN', 'ESTÁNDARES INTERNACIONALES', 'INFORMES DE CUMPLIMIENTO'];
      case 9: return ['Gobernanza de la Ciberseguridad', 'Marco de Gestión de Ciberseguridad', 'Sistemas de Acceso', 'Gestión de Seguridad Técnica', 'Amenazas y Gestión de Incidentes', 'Gestión de Seguridad',
        'Gestión de Continuidad del Negocio', 'Seguimiento y Mejora de la Seguridad', 'De los Reportes'];
      default: return genericDomains;
    }
  }

  const radarDataset = (frameworkId) => {
    switch (frameworkId) {
      case 1: return [compliance.data['GV']?.value || 0, compliance.data['ID']?.value || 0, compliance.data['PR']?.value || 0, compliance.data['DE']?.value || 0, compliance.data['RS']?.value || 0, compliance.data['RC']?.value || 0];
      case 2: return Array.from({ length: 4 }, (_, i) => {
        const ref = (i + 5).toString();
        return compliance.data[ref]?.value || 0;
      });
      case 4: return Array.from({ length: 18 }, (_, i) => {
        const ref = (i + 1).toString();
        return compliance.data[ref]?.value || 0;
      });
      case 6: return Array.from({ length: 6 }, (_, i) => {
        const ref = (i + 1).toString();
        return compliance.data[ref]?.value || 0;
      });
      case 7: return Array.from({ length: 10 }, (_, i) => {
        const ref = (i + 1).toString();
        return compliance.data[ref]?.value || 0;
      });
      case 8: return Array.from({ length: 6 }, (_, i) => {
        const ref = (i + 1).toString();
        return compliance.data[ref]?.value || 0;
      });
      case 9: return Array.from({ length: 9 }, (_, i) => {
        const ref = (i + 1).toString();
        return compliance.data[ref]?.value || 0;
      });
      // Keyed by domain name, which is what getCoreFunction returns for these
      // catalogues — the two have to agree or the radar reads zero.
      default: return genericDomains.map((domain) => compliance.data[domain]?.value || 0);
    }
  }

  const radarData = {
  labels: radarLabels(props.framework.id),
  datasets: [
    {
      label: 'Cumplimiento - Funciones de Ciberseguridad',
      data: radarDataset(props.framework.id),
      backgroundColor: 'rgba(49, 66, 163, 0.4)',
      borderColor: 'rgba(49, 66, 163, 1)',
      borderWidth: 3,
      pointBackgroundColor: 'rgba(75, 192, 192, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(75, 192, 192, 1)',
    },
  ],
  };

  // The frameworks whose radar was laid out by hand. Anything else is a
  // loaded catalogue whose axis count and label length are not known in
  // advance, so they are measured rather than assumed — 18 long Spanish
  // domain names at 11px overlap into an unreadable ring.
  const tunedRadar = [1, 2, 4, 6, 7, 8, 9].includes(props.framework?.id);

  const radarOptions = {
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(128, 128, 128, 0.2)', // Angle lines color
        },
        grid: {
          color: 'rgba(128, 128, 128, 0.2)', // Grid lines color
        },
        pointLabels: {
          color: '#FFF',
          font: {
            size: props.framework?.id === 4 || (!tunedRadar && radarData.labels.length > 10) ? 8 : 11
          },
          callback: function(label) {
            if (!tunedRadar || props.framework?.id === 4 || props.framework?.id === 6 || props.framework?.id === 7) {
              const words = label.split(' ');
              const lines = [];
              let currentLine = '';
              
              words.forEach(word => {
                if (currentLine.length + word.length > 20) {
                  lines.push(currentLine.trim());
                  currentLine = word;
                } else {
                  currentLine += (currentLine ? ' ' : '') + word;
                }
              });
              if (currentLine) {
                lines.push(currentLine.trim());
              }
              return lines;
            }
            return label;
          }
        },
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#FFF', // Legend labels color
        },
      },
    },
    };

  const doughnutData = {
    labels: ['Completado', 'Pendiente'],
    datasets: [
      {
        data: [progress, 100 - progress],
        backgroundColor: ['rgb(49, 66, 163', '#D3D3D3'],
        hoverBackgroundColor: ['rgb(49, 66, 163', '#D3D3D3'],
        borderWidth: 0,
      },
    ],
  };
  
  const doughnutOptions = {
    cutout: '80%',
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const counters = props.getCounters();

  const updateEngagementStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(Config.APIBaseURL + `/engagement/update/${props.id}`, 
        { 
          status: newStatus,
          previous_status: status
        }, 
        {
          headers: {
            'Content-Type': 'application/json',
            'access_token': `Bearer ${token}`
          },
          withCredentials: true
        }
      );
      
      if (response.data) {
        // Check conditions before updating status
        if (status === 'finished' && newStatus === 'submitted') {
          await fetchClarificationStatuses();
        }
        setStatus(newStatus);
      }
    } catch (error) {
      console.error('Error updating engagement status:', error);
    }
  };

  useEffect(() => {
    if (props.framework.id) {
      fetchControls(props.framework.id);
    }
    if (status !== 'not started') {
      fetchGapResults(props.id);
    }
  }, [props.framework, status]);

  useEffect(() => {
    if (!restoreFlag.current) {
      setRestoreEnabled(true);
      setSaveEnabled(true);
    } else {
      restoreFlag.current = false;
      saveFlag.current = false;
    }
  }, [compliance]);

  useEffect(() => {
    if (selectedCounter) {
      const updatedCounter = [...(compliance.data[counters.ref]?.counter || []), selectedCounter.description];
      setCompliance((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          [counters.ref]: {
            ...prev.data[counters.ref],
            counter: updatedCounter,
          },
        },
      }));
      setCountermeasureTexts((prev) => ({
        ...prev,
        [counters.ref]: updatedCounter.join('\n'),
      }));
      setSelectedCounter(null);
    }
  }, [selectedCounter, compliance.data]);

  const fetchControls = async (id) => {
    try {
      // This access to props.controlsCache should be guarded to avoid errors
      // and should not block the component rendering
      if (props.framework && 
          props.framework.id && 
          props.controlsCache && 
          props.controlsCache.current && 
          props.controlsCache.current[props.framework.id]) {
        // Use cached data asynchronously to avoid blocking render
        setTimeout(() => {
          setControls(props.controlsCache.current[props.framework.id]);
        }, 0);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(Config.APIBaseURL + '/controls/' + id, {
        headers: {
          'access_token': `Bearer ${token}`
        },
        withCredentials: true
      });
      
      const controlsData = response.data.controls_es;
      setControls(controlsData);
      
      // Cache the controls safely
      if (props.framework && 
          props.framework.id && 
          props.controlsCache && 
          props.controlsCache.current) {
        props.controlsCache.current[props.framework.id] = controlsData;
      }
    } catch (error) {
      console.error('Error fetching controls:', error);
    }
  };

  const fetchGapResults = async (id) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(Config.APIBaseURL + `/gap_results/${id}`, {
        headers: {
          'access_token': `Bearer ${token}`
        },
        withCredentials: true
      });
      setCompliance(response.data);
      setProgress(response.data.meta.progress);
      setGenScore(response.data.meta.genCompliance / 100);
    } catch (error) {
      console.error('Error fetching gap results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplianceChange = (controlRef, value) => {
    setDropdownSelections(prev => {
      const newState = {
        ...prev,
        [controlRef]: value
      };
      return newState;
    });
    
    setCompliance(prevCompliance => {
      // Initialize updatedCompliance with existing data and meta
      const updatedCompliance = {
        data: { ...prevCompliance.data, [controlRef]: { ...prevCompliance.data[controlRef], value } },
        meta: { ...prevCompliance.meta }
      };

      // Helper to check if control is applicable
      const isApplicable = (ref) => {
        return updatedCompliance.data[ref]?.value !== 4;
      };

      // Existing helper functions...
      const isSubControl = (ref) => {
        switch (props.framework.id) {
          case 1:
            return ref.length === 8; // For framework 1, subcontrols have 8 characters
          case 2:
          case 4:
          case 6:
          case 7:
          case 8:
          case 9:
            return ref.indexOf('.') !== -1; // For frameworks 4, 6, 8, 9, subcontrols contain a period
          default:
            return isGenericControl(ref);
        }
      };

      // These take the whole control, not just its ref: a catalogue with no
      // aggregate rows has nothing to derive a grouping from by chopping up a
      // ref, and its own domain/subdomain columns say it outright.
      const getParentControlRef = (control) => {
        const ref = control.ref;
        switch (props.framework.id) {
          case 1:
            return ref.substring(0, 5); // For framework 1, parent is first 5 chars
          case 2:
          case 4:
          case 6:
          case 7:
          case 8:
          case 9:
            return ref.split('.')[0]; // For frameworks 4, 6, 8, 9, parent is before the period
          default:
            return control.subdomain || control.domain || ref;
        }
      };

      const getCoreFunction = (control) => {
        const ref = control.ref;
        switch (props.framework.id) {
          case 1:
            return ref.substring(0, 2);
          case 2:
          case 4:
          case 6:
          case 7:
          case 8:
          case 9:
            return ref.split('.')[0];
          default:
            return control.domain || ref.split('.')[0];
        }
      };

      // First, let's identify all controls and their categories
      const controlsByParent = {};
      const controlsForCoreFunction = {};
      
      // Group all controls by their parent and core function, regardless of applicability
      controls.forEach(control => {
        if (isSubControl(control.ref)) {
          const coreFunction = getCoreFunction(control);
          const parentRef = getParentControlRef(control);
          
          // Initialize arrays if needed
          if (!controlsByParent[parentRef]) controlsByParent[parentRef] = [];
          if (!controlsForCoreFunction[coreFunction]) controlsForCoreFunction[coreFunction] = [];
          
          // Add the control to its groups
          controlsByParent[parentRef].push(control.ref);
          controlsForCoreFunction[coreFunction].push(control.ref);
        }
      });
      
      // Convert dropdown ids to scores using complianceOptions
      const convertToScore = (id) => {
        const option = complianceOptions.find(option => option.id === id);
        return option ? option.value : 0;
      };
      
      // Calculate averages for each parent and core function
      const summaryArray = [];
      
      // Process parent controls
      Object.keys(controlsByParent).forEach(parentRef => {
        const childControls = controlsByParent[parentRef];
        // Only consider applicable controls for the calculation
        const applicableControls = childControls.filter(ref => isApplicable(ref));
        
        // If there are applicable controls, calculate average
        if (applicableControls.length > 0) {
          const controlScores = applicableControls.map(ref => 
            convertToScore(updatedCompliance.data[ref]?.value)
          );
          const averageScore = Math.round(
            controlScores.reduce((sum, score) => sum + score, 0) / controlScores.length * 100
          );
          summaryArray.push({ key: parentRef, averageScore });
        } else if (childControls.length > 0) {
          // All controls are N/A - parent is also considered N/A
          updatedCompliance.data[parentRef] = {
            ...updatedCompliance.data[parentRef],
            value: 4  // Mark parent as N/A if all children are N/A
          };
        }
      });
      
      // Process core functions
      Object.keys(controlsForCoreFunction).forEach(coreFunction => {
        const functionControls = controlsForCoreFunction[coreFunction];
        // Only consider applicable controls for the calculation
        const applicableControls = functionControls.filter(ref => isApplicable(ref));
        
        // If there are applicable controls, calculate average
        if (applicableControls.length > 0) {
          const controlScores = applicableControls.map(ref => 
            convertToScore(updatedCompliance.data[ref]?.value)
          );
          const averageScore = Math.round(
            controlScores.reduce((sum, score) => sum + score, 0) / controlScores.length * 100
          );
          summaryArray.push({ key: coreFunction, averageScore });
        } else if (functionControls.length > 0) {
          // All controls are N/A - core function is also considered N/A
          updatedCompliance.data[coreFunction] = {
            ...updatedCompliance.data[coreFunction],
            value: 4  // Mark core function as N/A if all children are N/A
          };
        }
      });

      // Update compliance data with calculated averages
      summaryArray.forEach(summary => {
        updatedCompliance.data[summary.key] = {
          ...updatedCompliance.data[summary.key],
          value: summary.averageScore
        };
      });

      // Calculate progress and general compliance using only applicable controls
      const assessableControls = controls.filter(control => 
        isSubControl(control.ref) && isApplicable(control.ref)
      );
      
      // Handle the case of no applicable controls
      if (assessableControls.length === 0) {
        updatedCompliance.meta = {
          ...updatedCompliance.meta,
          progress: 100, // All controls are N/A, so progress is 100%
          genCompliance: 100 // All controls are N/A, so compliance is 100%
        };
        setProgress(100);
        setGenScore(1);
        
        return updatedCompliance;
      }
      
      // Calculate progress with applicable controls only
      const totalFields = assessableControls.length;
      const completedFields = assessableControls.filter(control => {
        const option = complianceOptions.find(option => option.id === updatedCompliance.data[control.ref]?.value);
        return option && option.id !== 0; // Exclude 'pending' fields
      }).length;
      const newProgress = Math.round((completedFields / totalFields) * 100);

      // Calculate total compliance percentage with applicable controls only
      const totalComplianceScore = assessableControls.reduce((sum, control) => {
        const option = complianceOptions.find(option => option.id === updatedCompliance.data[control.ref]?.value);
        return sum + (option ? option.value : 0);
      }, 0);
      const genCompliance = Math.round((totalComplianceScore / totalFields) * 100);

      // Update meta data with new calculations
      updatedCompliance.meta = {
        ...updatedCompliance.meta,
        progress: newProgress,
        genCompliance: genCompliance
      };

      // Update progress and genScore state
      setProgress(newProgress);
      setGenScore(genCompliance / 100);

      return updatedCompliance;
    });
  };

  // Add a state to track textarea content
  const [commentTexts, setCommentTexts] = useState({});

  // The comment handling function
  const handleCommentsChange = (controlRef, comment, isFromChat = false) => {
    if (isFromChat) {
      // For chat messages, append to existing comment
      const currentComment = compliance.data[controlRef]?.comment || '';
      const newComment = currentComment ? `${currentComment}\n${comment}` : comment;
      
      setCompliance(prev => ({
        ...prev,
        data: {
          ...prev.data,
          [controlRef]: {
            ...prev.data[controlRef],
            comment: newComment
          }
        }
      }));

      // Force textarea to update by triggering a re-render
      const textarea = document.getElementById(`comments-${controlRef}`);
      if (textarea) {
        textarea.value = newComment;
      }
    } else {
      // For manual textarea edits, just update with new value
      setCompliance(prev => ({
        ...prev,
        data: {
          ...prev.data,
          [controlRef]: {
            ...prev.data[controlRef],
            comment: comment
          }
        }
      }));
    }
  };

  // Add state for managing invalid files modal
  const [showInvalidFileModal, setShowInvalidFileModal] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState([]);

  // Update handleFileUpload to just store the files in state without uploading
  const handleFileUpload = (e, controlRef) => {
    e.preventDefault();
    e.stopPropagation();
    
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    
    if (files && files.length > 0) {
      const invalid = Array.from(files).filter(file => !ALLOWED_FILE_TYPES.includes(file.type));
      const valid = Array.from(files).filter(file => ALLOWED_FILE_TYPES.includes(file.type));
      
      if (invalid.length > 0) {
        setInvalidFiles(invalid.map(f => f.name));
        setShowInvalidFileModal(true);
      }

      if (valid.length > 0) {
        // Update the compliance data structure with the new files
        setCompliance(prevCompliance => ({
          ...prevCompliance,
          data: {
            ...prevCompliance.data,
            [controlRef]: {
              ...prevCompliance.data[controlRef],
              files: [
                ...(prevCompliance.data[controlRef]?.files || []),
                ...valid // Store the actual File objects
              ]
            }
          }
        }));
        setSaveEnabled(true);
      }
    }
  };

  // Update handleFileDelete to just remove from state
  const handleFileDelete = (e, controlRef, fileName) => {
    e.preventDefault();
    e.stopPropagation();

    setCompliance(prevCompliance => ({
      ...prevCompliance,
      data: {
        ...prevCompliance.data,
        [controlRef]: {
          ...prevCompliance.data[controlRef],
          files: prevCompliance.data[controlRef]?.files.filter(
            file => file.name !== fileName
          )
        }
      }
    }));
    
    setSaveEnabled(true);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileUpload(e);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Keep the original handleSave structure but add file handling
  const handleSave = async () => {
    if (Object.keys(compliance.data).length === 0 && Object.keys(compliance.meta).length === 0) {
      console.warn('Compliance object is empty. Save operation aborted.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Create FormData
      const formData = new FormData();
      
      // Add the compliance data with guaranteed engagementId
      const complianceWithEvidence = {
        ...compliance,
        meta: {
          ...compliance.meta,
          engagementId: props.id // Explicitly ensure engagementId is included
        },
        evidence: evidenceDocuments.map(evidence => ({
          id: evidence.id,
          controls: evidence.controls,
          description: evidence.description,
          timestamp: evidence.timestamp,
          fileNames: evidence.files.map(file => file.name)
        }))
      };
      
      formData.append('compliance', JSON.stringify(complianceWithEvidence));
      
      // Add all files from evidence documents
      evidenceDocuments.forEach(evidence => {
        evidence.files.forEach(file => {
          // Add a prefix to avoid name collisions
          formData.append(`evidence_${evidence.id}_files`, file);
        });
      });

      // Send to server
      const response = await axios.post(
        Config.APIBaseURL + '/gap_results',
        formData,
        {
          headers: {
            'access_token': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      setShowSaveMessage(true);
      setTimeout(() => {
        setShowSaveMessage(false);
      }, 3000);
      
      status === 'not started' && await updateEngagementStatus('in progress');
      setSaveEnabled(false);
      saveFlag.current = true;
      
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleFinish = async () => {
    if (progress < 100) {
      console.warn('Unable to finish engagement. Progress is less than 100%.');
      return;
    }
    try {
      status === 'in progress' && await updateEngagementStatus('finished');
      setAssessmentModalOpen(false);
      setResultsModalOpen(true);
    } catch (error) {
      console.error('Error finishing engagement:', error);
    }
  };

  const handleRestore = () => {
      setCompliance({ data: {}, meta: {} });
      setProgress(0);
      setGenScore(0);
      setRestoreEnabled(false);
      restoreFlag.current = true;
      setSaveEnabled(false);
      saveFlag.current = true;
  };

  const handleAddCounter = (controlRef, description) => {
    const currentCounters = compliance.data[controlRef]?.counter || [];
    if (currentCounters.includes(description)) {
      console.log('Countermeasure already exists');
      return;
    }
  
    setCompliance(prevCompliance => ({
      ...prevCompliance,
      data: {
        ...prevCompliance.data,
        [controlRef]: {
          ...prevCompliance.data[controlRef],
          counter: [
            ...(prevCompliance.data[controlRef]?.counter || []),
            description
          ]
        }
      }
    }));
  
    setDisplayedLines(prevLines => prevLines.filter(displayedLine => displayedLine !== description));
  
    //setSelectedFramework(null);
    //setSelectedDomain(null);
    //setSelectedSubdomain(null);
  
    setSaveEnabled(true);
  };

  const handleDeleteLine = (lineToDelete) => {
    setDisplayedLines(prevLines => prevLines.filter(line => line !== lineToDelete));
  };

  const genAI = async (control) => {
    try {
      // Reset animation state
      setDisplayedLines([]);
      setCurrentLine('');
      setIsAnimating(false);
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
  
      setIsGenerating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${Config.APIBaseURL}/genai/`, { control }, {
        headers: {
          'Content-Type': 'application/json',
          'access_token': `Bearer ${token}`
        },
        withCredentials: true
      });
      setIsGenerating(false);

      const content = response?.data?.data?.choices?.[0]?.message?.content || '';

      // Robust parsing: split by newlines, trim, strip common bullet markers
      let lines = content
        .split(/\n+/)
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => l.replace(/^[•\-\*\u2022]\s*/, ''));

      // Fallback: if nothing parsed but content has bullets, split by bullet
      if (lines.length === 0 && content.includes('•')) {
        lines = content
          .split('•')
          .map((l) => l.trim())
          .filter(Boolean);
      }

      setAICounter(lines);
    } catch (error) {
      console.error('Error generating countermeasures:', error);
      setIsGenerating(false);
    }
  };  

  const animateMessage = (messages) => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }
  
    setIsAnimating(true);
    setDisplayedLines([]);
    setCurrentLine('');
  
    let currentMessageIndex = 0;
    let charIndex = 0;
    const charsPerInterval = 10;
  
    const interval = setInterval(() => {
      if (currentMessageIndex >= messages.length) {
        clearInterval(interval);
        setIsAnimating(false);
        animationIntervalRef.current = null;
        return;
      }
  
      const currentMessage = messages[currentMessageIndex];
      if (charIndex < currentMessage.length) {
        setCurrentLine(prev => currentMessage.slice(0, charIndex + charsPerInterval));
        charIndex += charsPerInterval;
      } else {
        setDisplayedLines(prev => [...prev, currentMessage]);
        setCurrentLine('');
        currentMessageIndex++;
        charIndex = 0;
      }
    }, 50);
    animationIntervalRef.current = interval;
  };

  useEffect(() => {
    if (isGenerating) {
      let dotCount = 0;
      const interval = setInterval(() => {
        setGenMessage(`Generando${'.'.repeat(dotCount % 4)}`);
        dotCount++;
      }, 360);
  
      return () => clearInterval(interval);
    }
  }, [isGenerating]);
  
  useEffect(() => {
    if (AICounter.length > 0) {
      animateMessage(AICounter);
    }
  }, [AICounter]);

  useEffect(() => {
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (resultsModalOpen && resultsTableRef.current) {
      resultsTableRef.current.scrollLeft = resultsTableRef.current.scrollWidth;
    }
  }, [resultsModalOpen]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const getBGName = (ref, frameworkId) => {
    switch (frameworkId) {
      case 1:
        return ref.length === 5 ? "bg-slate-900" : "";
      case 2:
      case 4:
      case 6:
      case 7:
      case 8:
      case 9:
        return ref.indexOf('.') === -1 ? "bg-slate-900" : "";
      default:
        return isGenericControl(ref) ? "" : "bg-slate-900";
    }
  };

  const groupByFramework = (counters) => {
    const grouped = {};
    Object.entries(counters || {}).forEach(([key, data]) => {
      const { framework_name, domain, subdomain, ref, description } = data;
      
      if (!grouped[framework_name]) {
        grouped[framework_name] = {};
      }
      if (!grouped[framework_name][domain]) {
        grouped[framework_name][domain] = {};
      }
      if (!grouped[framework_name][domain][subdomain]) {
        grouped[framework_name][domain][subdomain] = [];
      }
      
      grouped[framework_name][domain][subdomain].push({
        ref,
        description
      });
    });
    return grouped;
  };  

  // Use useMemo for expensive calculations or formatting
  const customerLogo = useMemo(() => {
    if (!props.customer || !props.customer[2]) return null;
    // Format the logo or any other expensive operations
    return props.customer[2];
  }, [props.customer]);

  const [evidenceDocuments, setEvidenceDocuments] = useState([]);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [currentUploadedFiles, setCurrentUploadedFiles] = useState([]);
  const [selectedControls, setSelectedControls] = useState([]);
  const [evidenceDescription, setEvidenceDescription] = useState('');

  const handleEvidenceFileUpload = (e) => {
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    
    if (files && files.length > 0) {
      const invalid = Array.from(files).filter(file => !ALLOWED_FILE_TYPES.includes(file.type));
      const valid = Array.from(files).filter(file => ALLOWED_FILE_TYPES.includes(file.type));
      
      if (invalid.length > 0) {
        setInvalidFiles(invalid.map(f => f.name));
        setShowInvalidFileModal(true);
      }

      if (valid.length > 0) {
        setCurrentUploadedFiles([...currentUploadedFiles, ...valid]);
      }
    }
  };

  const handleEvidenceFileDelete = (index) => {
    setCurrentUploadedFiles(currentUploadedFiles.filter((_, i) => i !== index));
  };

  const handleControlSelection = (controlRef) => {
    if (selectedControls.includes(controlRef)) {
      setSelectedControls(selectedControls.filter(ref => ref !== controlRef));
    } else {
      setSelectedControls([...selectedControls, controlRef]);
    }
  };

  const handleAddEvidence = () => {
    if (currentUploadedFiles.length === 0 || selectedControls.length === 0) {
      return; // Maybe show an error message
    }
    
    const newEvidence = {
      id: Date.now().toString(),
      files: currentUploadedFiles,
      controls: selectedControls,
      description: evidenceDescription,
      timestamp: new Date().toISOString()
    };
    
    setEvidenceDocuments([...evidenceDocuments, newEvidence]);
    
    setCurrentUploadedFiles([]);
    setSelectedControls([]);
    setEvidenceDescription('');
    setEvidenceModalOpen(false);
    setSaveEnabled(true);
  };

  const handleEvidenceDelete = (evidenceId) => {
    setEvidenceDocuments(evidenceDocuments.filter(doc => doc.id !== evidenceId));
    setSaveEnabled(true);
  };

  const fetchClarificationStatuses = async () => {
    try {
      if (!props.id) return;
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${Config.APIBaseURL}/engagement/clarifications/${props.id}`,
        {
          headers: {
            'access_token': `Bearer ${token}`
          },
          withCredentials: true
        }
      );

      // Transform the array into an object keyed by control_ref
      const statusesMap = response.data.reduce((acc, clarification) => {
        acc[clarification.control_ref] = clarification;
        return acc;
      }, {});

      setClarificationStatuses(statusesMap);
    } catch (error) {
      console.error('Error fetching clarification statuses:', error);
      setClarificationStatuses({});
    }
  };

  useEffect(() => {
    if (status === 'submitted' && props.id) {
      fetchClarificationStatuses();
    }
  }, [props.id, status]);

  const handleClarificationRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${Config.APIBaseURL}/engagement/clarification-request/${props.id}`,
        {
          control_ref: selectedControl,
          comment: clarificationComment,
        },
        {
          headers: {
            'access_token': `Bearer ${token}`
          },
          withCredentials: true
        }
      );
      
      // Clear the form
      setClarificationComment('');
      
      // Fetch the updated clarification statuses
      await fetchClarificationStatuses();
      
      // Now that we have the updated data, we can close the modal
      setShowClarificationModal(false);
      setSelectedControl(null);
      
    } catch (error) {
      console.error('Error sending clarification request:', error);
  
      if (error.response && error.response.status === 409) {
        setNotificationMessage('Ya existe una solicitud de aclaración para este control.');
      } else {
        setNotificationMessage('Error al enviar la solicitud de aclaración.');
      }
      
      setNotificationType('error');
      setNotificationOpen(true);
    }
  };

  const handleClarificationResponse = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${Config.APIBaseURL}/engagement/clarification-response/${props.id}`,
        {
          control_ref: selectedControl,
          comment: clarificationComment
        },
        {
          headers: {
            'access_token': `Bearer ${token}`
          },
          withCredentials: true
        }
      );

      // Instead of manually removing the status, fetch fresh data
      await fetchClarificationStatuses();

      // Close modal and reset state
      setShowClarificationModal(false);
      setSelectedControl(null);
      setClarificationComment('');

    } catch (error) {
      console.error('Error responding to clarification:', error);
    }
  };

  const handleFinalizeClarification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${Config.APIBaseURL}/engagement/clarification-response/${props.id}`,
        {
          control_ref: selectedControl,
          comment: clarificationComment
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'access_token': `Bearer ${token}`
          },
          withCredentials: true
        }
      );

      if (response.status === 200) {
        // Fetch the updated data first
        await fetchClarificationStatuses();
        
        // Then clear input and close modal
        setClarificationComment('');
        setShowClarificationModal(false);
      }
    } catch (error) {
      console.error('Error finalizing clarification:', error);
    }
  };

  useEffect(() => {
    console.log(props.files);
    if (props.files && Array.isArray(props.files) && props.files.length > 0) {
      console.log("Setting evidence documents with files:", props.files);
      // Transform the file paths into evidence document format
      const initialEvidence = {
        id: 'initial',
        files: props.files.map(filePath => ({
          name: filePath.split('/').pop() // Get the filename from the path
        })),
        controls: [], // Initially empty, can be populated if needed
        description: 'Material de apoyo inicial', // Or any other appropriate description
        timestamp: new Date().toISOString()
      };
      setEvidenceDocuments([initialEvidence]);
    }
  }, [props.files]);

  return (
    <>
      <tr>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="flex items-center">
            <label className="inline-flex">
              <span className="sr-only">Select</span>
              <input id={props.id} className="form-checkbox" type="checkbox" onChange={props.handleClick} checked={props.isChecked} />
            </label>
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 min-w-[200px] max-w-[200px]">
          <div className="overflow-hidden">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200 block truncate" title={props.title}>
              {props.title}
            </span>
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
              {props.customer && props.customer[1] ? props.customer[1] : 'n/a'}
            </span>
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex items-center border-l-2 border-slate-300 dark:border-slate-600 pl-3">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {props.framework.name}
            </span>
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 ${
            status === 'submitted' 
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' 
              : status === 'not started'
                ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300'
                : status === 'in progress'
                  ? 'bg-sky-50 dark:bg-sky-900/30 text-amber-500 dark:text-indigo-400'
                  : status === 'finished'
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300'
            }`}>
            {status}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-wrap">
          <div className="inline-flex items-center justify-center px-3 py-1 font-medium border-l-4 border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
            {progress}%
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-wrap">
          <div className={`inline-flex items-center justify-center px-3 py-1 font-medium border-l-4 relative w-24 ${
            genScore * 100 >= 90
              ? 'border-emerald-600 text-emerald-700 dark:text-emerald-300'
              : genScore * 100 >= 70
                ? 'border-teal-600 text-teal-700 dark:text-teal-300'
                : genScore * 100 >= 50
                  ? 'border-amber-600 text-amber-700 dark:text-amber-300'
                  : 'border-rose-600 text-rose-700 dark:text-rose-300'
          }`}>
            <div 
              className={`absolute inset-0 ${
                genScore * 100 >= 90
                  ? 'bg-emerald-600/30'
                  : genScore * 100 >= 70
                    ? 'bg-teal-600/30'
                    : genScore * 100 >= 50
                      ? 'bg-amber-600/30'
                      : 'bg-rose-600/30'
              }`} 
              style={{ 
                width: `${Math.round(genScore * 100)}%`,
                left: '4px' // account for border-l-4
              }}
            />
            <span className="relative z-10 w-full text-center">{Math.round(genScore * 100)}%</span>
          </div>
        </td>
        {/*
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-wrap">
          <div className="font-medium text-slate-800 dark:text-slate-400">{props.comments}</div>
        </td>
        */}
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
          <div className="flex items-center">
            <button
              className={`text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 ${descriptionOpen && 'rotate-180'}`}
              aria-expanded={descriptionOpen}
              onClick={() => setDescriptionOpen(!descriptionOpen)}
              aria-controls={`description-${props.id}`}
            >
              <span className="sr-only">Menu</span>
              <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                <path d="M16 20l-5.4-5.4 1.4-1.4 4 4 4-4 1.4 1.4z" />
              </svg>
            </button>
            <button className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 rounded-full">
              <span className="sr-only">Edit</span>
              <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                  <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
              </svg>
            </button>
            <button className="text-rose-500 hover:text-rose-600 rounded-full" onClick={() => { setShowDeleteMessage(true); }}>
              <span className="sr-only">Delete</span>
              <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                  <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                  <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
              </svg>
            </button>
            {showDeleteMessage && (
            <div id="confirm-delete" className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 border-slate-300 ease-in-out">
              <div className="grid grid-cols-2 items-center w-full max-w-sm p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-700" role="alert" style={{ gridTemplateColumns: '0.5fr 3fr' }}>
                <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-indigo-600 bg-indigo-600 rounded-full dark:bg-indigo-900 dark:text-indigo-600 mr-4">
                  <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                    <path d="M12 4.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z"/>
                    <path d="M12 14a1 1 0 01-1-1V8a1 1 0 112 0v5a1 1 0 01-1 1zm-1.5 2.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z"/>
                  </svg>
                  <span className="sr-only">Check icon</span>
                </div>
                <div className="text-sm text-slate-300">¿Está seguro de que quiere eliminar esta tarea?</div>
                <div className="col-span-2">
                  <div className="col-span-2 flex justify-end space-x-2 mt-4">
                    <button className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300" onClick={(e) => { e.stopPropagation(); setShowDeleteMessage(false); }}>Close</button>
                    <button className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white" onClick={(e) => { e.stopPropagation(); setShowDeleteMessage(false); props.onDeleteEngagement([props.id]); }}>Eliminar</button>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </td>
      </tr>
      <tr id={`description-${props.id}`} role="region" className={`${!descriptionOpen && 'hidden'}`}>
          <td colSpan="10" className="px-2 first:pl-5 last:pr-5 py-3">
            <div className="flex grid grid-cols-4 gap-2 items-center bg-slate-50 dark:bg-slate-900/30 dark:text-slate-400 p-3 -mt-3">
              <span>
                <img src={imageSrc(customerLogo)} width="160"></img>
                { props.type && props.type[1] === 'GAP' && (
                <div className="mt-4">
                  {  status === 'not started' ? (
                    <button className="btn bg-indigo-500 hover:bg-indigo-600 text-white w-40" aria-controls="scan-modal" onClick={(e) => { e.stopPropagation(); setAssessmentModalOpen(true); }}>
                          <span className="xs:block">Iniciar Análisis</span>
                    </button>
                    ) :
                  status === 'in progress' ? (
                    <button className="btn bg-indigo-500 hover:bg-indigo-600 text-white w-40" aria-controls="scan-modal" onClick={(e) => { e.stopPropagation(); fetchGapResults(props.id); setAssessmentModalOpen(true); }}>
                          <span className="xs:block">Continuar Análisis</span>
                    </button>
                    ) : (
                    <button className="btn bg-indigo-500 hover:bg-indigo-600 text-white w-40" aria-controls="scan-modal" onClick={(e) => { e.stopPropagation(); fetchGapResults(props.id); setResultsModalOpen(true); }}>
                      <span className="xs:block">Resultados</span>
                    </button>
                    )
                  }
                </div>
                )}
              </span>
              <div className="dark:border-slate-700 pt-4 col-span-3">
              <div className="flex justify-between items-center mb-4">
                <span className="flex">
                  <h3 className="text-md font-semibold text-slate-800 dark:text-slate-100">Material de Apoyo</h3>
                  <button 
                    className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ml-4"
                    onClick={() => setEvidenceModalOpen(true)}
                  >
                    <svg className="w-3 h-3 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                      <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                    </svg>
                  </button>
                </span>
              </div>
              
              {evidenceDocuments.length === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">No supporting materials added yet</p>
              ) : (
                <div className="overflow-x-auto w-full">
                  <table className="table-auto min-w-full">
                    <thead className="text-xs font-semibold uppercase text-slate-500 bg-slate-50 dark:bg-slate-700 dark:text-slate-400">
                      <tr>
                        <th className="px-2 py-3 whitespace-nowrap">Archivos</th>
                        <th className="px-2 py-3 whitespace-nowrap">Descripción</th>
                        <th className="px-2 py-3 whitespace-nowrap">Controles Relacionados</th>
                        <th className="px-2 py-3 whitespace-nowrap">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                      {evidenceDocuments.map((evidence) => (
                        <tr key={evidence.id}>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <ul className="list-disc list-inside">
                              {evidence.files.map((file, index) => (
                                <li key={index} className="text-xs">{file.name}</li>
                              ))}
                            </ul>
                          </td>
                          <td className="px-2 py-3 text-xs whitespace-nowrap">
                            {evidence.description}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <div className="flex flex-wrap gap-1">
                              {evidence.controls.map((controlRef) => (
                                <span 
                                  key={controlRef} 
                                  className="px-2 py-1 text-xs rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                                >
                                  {controlRef}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleEvidenceDelete(evidence.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                                <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
              {evidenceModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                  <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                    <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-slate-800 dark:text-slate-100">Agregar Material de Apoyo</h3>
                        <button
                          className="text-slate-400 hover:text-slate-500"
                          onClick={() => setEvidenceModalOpen(false)}
                        >
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                            <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      <div className="space-y-4">
                        {/* File Upload Area */}
                        <div>
                          <label className="block text-sm font-medium mb-1">Cargar Archivos</label>
                          <div
                            className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center hover:border-indigo-500 dark:hover:border-indigo-500 transition-colors cursor-pointer"
                            onClick={() => document.getElementById('evidence-file-upload').click()}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEvidenceFileUpload(e);
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <input
                              id="evidence-file-upload"
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleEvidenceFileUpload}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.bmp,.tiff,.tif"
                            />
                            <div className="text-center">
                              <svg className="w-10 h-10 mx-auto text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Arrastra y suelta archivos, o haz click para seleccionar</p>
                            </div>
                          </div>
                          
                          {/* Selected Files */}
                          {currentUploadedFiles.length > 0 && (
                            <div className="mt-3 max-h-32 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700">
                              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                                {currentUploadedFiles.map((file, index) => (
                                  <li key={index} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    <span className="text-sm text-slate-600 dark:text-slate-400 truncate">{file.name}</span>
                                    <button
                                      onClick={() => handleEvidenceFileDelete(index)}
                                      className="ml-2 text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-500 transition-colors"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        {/* Description Field */}
                        <div>
                          <label className="block text-sm font-medium mb-1" htmlFor="evidence-description">
                            Description
                          </label>
                          <textarea
                            id="evidence-description"
                            className="form-textarea w-full bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700"
                            rows="2"
                            placeholder="Describe lo que demuestra este material de apoyo..."
                            value={evidenceDescription}
                            onChange={(e) => setEvidenceDescription(e.target.value)}
                          ></textarea>
                        </div>
                        
                        {/* Control Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-1">Controles Relacionados</label>
                          <div className="max-h-48 overflow-y-auto border border-slate-300 dark:border-slate-700 rounded p-2">
                            <div className="grid grid-cols-3 gap-2">
                              {controls.map((control) => (
                                <div key={control.ref} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`control-${control.ref}`}
                                    checked={selectedControls.includes(control.ref)}
                                    onChange={() => handleControlSelection(control.ref)}
                                    className="form-checkbox"
                                  />
                                  <label htmlFor={`control-${control.ref}`} className="text-xs ml-2 truncate">
                                    {control.ref}: {control.description.substring(0, 30)}{control.description.length > 30 ? '...' : ''}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex justify-end space-x-3">
                        <button
                          className="btn-sm w-24 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300"
                          onClick={() => setEvidenceModalOpen(false)}
                        >
                          Cancelar
                        </button>
                        <button
                          className={`btn-sm w-24 bg-indigo-500 hover:bg-indigo-600 text-white ${
                            currentUploadedFiles.length === 0 || selectedControls.length === 0
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          onClick={handleAddEvidence}
                          disabled={currentUploadedFiles.length === 0 || selectedControls.length === 0}
                        >
                          Aceptar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </td>
      </tr>
      <NewAssessmentModal id="assessment-modal" modalOpen={assessmentModalOpen} setModalOpen={setAssessmentModalOpen} title={`Gap Analysis - ${props.framework.name}`} isMaximized={isMaximized} toggleMaximize={toggleMaximize}>
        <div id="assessment-header" className={`flex grid grid-cols-3 gap-2 items-start dark:bg-slate-900 dark:text-slate-400 px-3 py-3 sticky top-0 ${isMaximized ? 'w-full' : ''}`} style={{ height: isCollapsed ? '0' : '330px', overflowY: 'hidden', transition: 'height 0.5s ease' }}>
          <div className="flex grid grid-cols-3 items-start dark:text-slate-400 p-2">
            <img src={imageSrc(customerLogo)} width="200" height="240" className="self-star col-span-3"></img>
            <span className="font-bold text-sky-600 pt-6 row-start-2">Progress:</span>
            <div className="justify-center row-start-2 pt-5" style={{ width: '180px' }}> 
              <div style={{ position: 'relative', width: '50%'}}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#FFF',
                  }}
                >
                  {progress}%
                </div>
              </div>
            </div>
            <span className="font-bold text-sky-600 pt-5 row-start-3">Creado por: </span>
            <span className="text-white pt-5 ml-2 row-start-3 col-start-2 col-span-2">{props.owner || ''}</span>
            {props.updatedAt && (
              <>
              <span className="font-bold text-sky-600 pt-2 row-start-4">Actualizado:</span>
              <span className="text-white pt-2 row-start-4 col-start-2 col-span-2 ml-2">{new Date(props.updatedAt).toLocaleString()}</span>
              </>
            )}
            {props.updatedBy && (
              <>
              <span className="font-bold text-sky-600 pt-2 row-start-5">Actualizado por: </span>
              <span className="text-white pt-2 row-start-5 col-start-2 col-span-2 ml-2">{props.updatedBy || ''}</span>
              </>
            )}
          </div>
          <div className="bg-white dark:bg-slate-900 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 px-4">
            <Radar data={radarData} options={radarOptions} />
          </div>
          <div className="flex flex-col justify-center items-center self-start" style={{ width: '340px' }}>
          <label className="text-center mb-2 font-bold text-white py-6">
              Nivel de Cumplimiento General
            </label>
          <GaugeChart id="general-score" colors={["#DE3163", "#FFFF00", "#27AE60"]} arcWidth={0.1} needleScale={0.70} percent={ genScore } />
          </div>
          <button onClick={toggleCollapse} className="absolute bottom-2 right-2 bg-transparent text-gray-800 font-bold px-10 rounded">
            <div className="flex shrink-0 ml-2">
              <svg
                className={`w-4 h-4 shrink-0 ml-1 fill-current text-slate-400 ${!isCollapsed && 'rotate-180'}`}
                viewBox="0 0 12 12"
              >
                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
              </svg>
            </div>
          </button> 
        </div>
        <div className="px-0.5 py-0.5">
          <div id="assessment-table" className="overflow-x-auto" style={{ maxHeight: isCollapsed ? '450px' : '250px' }} >
            <table className="min-w-full bg-white dark:bg-slate-800 text-xs">
              <thead className="sticky top-0 bg-white dark:bg-slate-800 shadow-lg z-10">
                <tr>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[100px]">Ref</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700">Domain</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700">Subdomain</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700">Control</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[225px]">Assessment</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[220px]">Comments</th>
                  {/*<th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[170px]">Actions</th>*/}
                </tr>
              </thead>
              <tbody>
              { 
                controls ? (controls.length > 0 ?   
                controls.map(control => {
                  const selectedOption = dropdownSelections[control.ref] || complianceOptions.find(option => option.id === compliance.data[control.ref]?.value)?.id || 0;
                  return (
                <>
                <tr key={ control.id } className={compliance.data[control.ref]?.value === 4 ? "opacity-50 bg-gray-800" : ""}>
                  <td className={`px-4 py-2 border-b border-slate-200 dark:border-slate-700 ${getBGName(control.ref, props.framework.id)}`}>{ control.ref }</td>
                  {/*{ control.domain === 'GOBERNAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-amber-100 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'IDENTIFICAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-sky-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'PROTEGER' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-indigo-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'DETECTAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-orange-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'RESPONDER' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-rose-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'RECUPERAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-green-400 text-black font-bold">{ control.domain }</td>) :*/}
                  <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-indigo-900">{ control.domain }</td>
                  <td className={`px-4 py-2 border-b border-slate-200 dark:border-slate-700 ${getBGName(control.ref, props.framework.id)}`}>{ control.subdomain !== 'none' ? control.subdomain : ''}</td>
                  <td className={`px-4 py-2 border-b border-slate-200 dark:border-slate-700 ${getBGName(control.ref, props.framework.id)}`} style={{ whiteSpace: 'pre-line' }}>{ control.description !== 'none' ? control.description : ''}</td>
                  { isAssessable(control) ? (
                    <>
                    <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                      <div>
                        <DropdownAI 
                          options={complianceOptions}
                          style="w-full text-xs"
                          onChange={(value) => handleComplianceChange(control.ref, value)}
                          selectedOption={selectedOption}
                        />
                      </div>
                      <div className="group relative">
                        <button 
                          className={`btn hover:bg-slate-700 ${compliance.data[control.ref]?.value === 4 ? "opacity-50 cursor-not-allowed" : ""}`} 
                          aria-controls={`analyze-${control.id}`} 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            if (compliance.data[control.ref]?.value !== 4) { 
                              setExpandedRows((prev) => ({...prev, [control.id]: !prev[control.id],}));
                            }
                          }}
                          disabled={compliance.data[control.ref]?.value === 4}
                        >
                          <svg width="40px" height="40px" viewBox="0 0 512 512" version="1.1">
                              <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                  <g id="icon" fill="#808080" transform="translate(64.000000, 64.000000)">
                                      <path d="M320,64 L320,320 L64,320 L64,64 L320,64 Z M171.749388,128 L146.817842,128 L99.4840387,256 L121.976629,256 L130.913039,230.977 L187.575039,230.977 L196.319607,256 L220.167172,256 L171.749388,128 Z M260.093778,128 L237.691519,128 L237.691519,256 L260.093778,256 L260.093778,128 Z M159.094727,149.47526 L181.409039,213.333 L137.135039,213.333 L159.094727,149.47526 Z M341.333333,256 L384,256 L384,298.666667 L341.333333,298.666667 L341.333333,256 Z M85.3333333,341.333333 L128,341.333333 L128,384 L85.3333333,384 L85.3333333,341.333333 Z M170.666667,341.333333 L213.333333,341.333333 L213.333333,384 L170.666667,384 L170.666667,341.333333 Z M85.3333333,0 L128,0 L128,42.6666667 L85.3333333,42.6666667 L85.3333333,0 Z M256,341.333333 L298.666667,341.333333 L298.666667,384 L256,384 L256,341.333333 Z M170.666667,0 L213.333333,0 L213.333333,42.6666667 L170.666667,42.6666667 L170.666667,0 Z M256,0 L298.666667,0 L298.666667,42.6666667 L256,42.6666667 L256,0 Z M341.333333,170.666667 L384,170.666667 L384,213.333333 L341.333333,213.333333 L341.333333,170.666667 Z M0,256 L42.6666667,256 L42.6666667,298.666667 L0,298.666667 L0,256 Z M341.333333,85.3333333 L384,85.3333333 L384,128 L341.333333,128 L341.333333,85.3333333 Z M0,170.666667 L42.6666667,170.666667 L42.6666667,213.333333 L0,213.333333 L0,170.666667 Z M0,85.3333333 L42.6666667,85.3333333 L42.6666667,128 L0,128 L0,85.3333333 Z" id="Combined-Shape">

                          </path>
                                  </g>
                              </g>
                          </svg>
                          <div className="flex flex-col ml-2">
                            <span className="text-xs text-gray-300 ml-2">Analizar con IA</span>
                            <span className="ml-2 text-[9px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded-lg">Beta</span>
                          </div>
                        </button>
                        {/*
                        <style jsx>{`
                          .tooltip {
                            transition: opacity 0.15s;
                            transition-delay: 2s;
                            opacity: 0;
                            pointer-events: none;
                          }
                          .group:hover .tooltip {
                            opacity: 1;
                            pointer-events: auto;
                          }
                        `}</style>
                        <div className="tooltip absolute
                          bottom-full mb-2
                          left-1/2 -translate-x-1/2
                          bg-slate-700 text-slate-200 px-3 py-2 rounded-lg text-xs
                          shadow-lg border border-slate-700 w-64 z-50
                          after:content-[''] 
                          after:absolute 
                          after:left-1/2 
                          after:-translate-x-1/2
                          after:top-full 
                          after:border-8 
                          after:border-transparent
                          after:border-t-slate-700"
                        >
                          Usa nuestra IA para inferir el grado de cumplimiento
                        </div>
                        */}
                      </div>
                    </td>
                    <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                      <textarea 
                        id={`comments-${control.ref}`}
                        className="form-textarea w-full bg-slate-800 border-slate-700 hover:border-slate-600 text-slate-300 text-xs hover:text-slate-200 focus:border-slate-600"
                        rows="4"
                        defaultValue={compliance.data[control.ref]?.comment || ''}
                        key={compliance.data[control.ref]?.comment} // Add key to force re-render when comment changes
                        onBlur={(e) => handleCommentsChange(control.ref, e.target.value, false)}
                        style={{ resize: 'none' }}
                      />
                      {/* File upload components removed, now handled by dedicated evidence area */}
                    </td>
                  </>
                  ) : (
                    <>
                    <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 text-sm font-bold bg-slate-900">
                      {compliance.data[control.ref]?.value === 4 ? "N/A" : `${compliance.data[control.ref]?.value || 0}%`}
                    </td>
                    <td className="bg-slate-900 border-b border-slate-700"></td>
                    <td className="bg-slate-900 border-b border-slate-700"></td>
                    </>
                  )}
                </tr>
                <tr id={`analyze-${control.id}`} role="region" className={`${!expandedRows[control.id] && 'hidden'}`}>
                  <td colSpan="7" className="p-0">
                    <div className="bg-slate-900 border-t border-b border-slate-700">
                      {/* Tabs */}
                      <div className="flex border-b border-slate-700">
                        <button
                          className={`px-4 py-2 text-sm font-medium ${
                            activeAnalysisTabs[control.id] !== 'vision'
                              ? 'text-indigo-500 border-b-2 border-indigo-500'
                              : 'text-slate-400 hover:text-slate-300'
                          }`}
                          onClick={() => setActiveAnalysisTabs(prev => ({...prev, [control.id]: 'chat'}))}
                        >
                          Chat Analysis
                        </button>
                        <button
                          className={`px-4 py-2 text-sm font-medium ${
                            activeAnalysisTabs[control.id] === 'vision'
                              ? 'text-indigo-500 border-b-2 border-indigo-500'
                              : 'text-slate-400 hover:text-slate-300'
                          }`}
                          onClick={() => setActiveAnalysisTabs(prev => ({...prev, [control.id]: 'vision'}))}
                        >
                          Document Analysis
                        </button>
                      </div>
                      
                      {/* Content */}
                      {activeAnalysisTabs[control.id] === 'vision' ? (
                        <AIVision 
                          className="h-[400px]" 
                          control={control}
                          onComplianceLevelDetected={(controlRef, level) => handleComplianceChange(controlRef, level)}
                          onAddComment={(controlRef, content) => handleCommentsChange(controlRef, content, true)}
                          evidenceDocuments={evidenceDocuments}
                        />
                      ) : (
                        <AIChat 
                          className="h-[400px]" 
                          control={control}
                          onComplianceLevelDetected={(controlRef, level) => handleComplianceChange(controlRef, level)}
                          onAddComment={(controlRef, content) => handleCommentsChange(controlRef, content, true)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
                </>
                )
                })
                : <tr><td colSpan={7}><p className="text-center py-4">No controls found</p></td></tr>) : <div><svg className="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                </svg><span className="ml-2">Loading</span></div>
              }
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 py-4">
          <div className="flex flex-wrap justify-end space-x-2">
            <button className="btn-sm bg-slate-600 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300" onClick={(e) => { e.stopPropagation(); setAssessmentModalOpen(false); }}>Close</button>
            <button className={`btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ${restoreEnabled ? '' : 'opacity-50 cursor-not-allowed'}`} disabled={!restoreEnabled} onClick={(e) => { e.stopPropagation(); handleRestore(); }}>Restablecer</button>
            <button className={`btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ${saveEnabled ? '' : 'opacity-50 cursor-not-allowed'}`} disabled={!saveEnabled} onClick={(e) => { e.stopPropagation(); handleSave(); }}>Guardar</button>
            <button className={`btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ${progress === 100 ? '' : 'opacity-50 cursor-not-allowed'}`} disabled={progress < 100} onClick={(e) => { e.stopPropagation(); setShowFinishMessage(true); }}>Finalizar</button>
          </div>
        </div>
        {showSaveMessage && (
        <div id="save-success" className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out">
          <div className="flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-700" role="alert">
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg dark:bg-green-800 dark:text-green-200">
              <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
              </svg>
              <span className="sr-only">Check icon</span>
            </div>
            <div className="ms-3 text-sm text-slate-300">Datos guardados con éxito</div>
          </div>
        </div>
        )}
        {showFinishMessage && (
        <div id="confirm-finish" className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out">
          <div className="grid grid-cols-2 items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800" role="alert" style={{ gridTemplateColumns: '0.5fr 3fr' }}>
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-indigo-600 bg-indigo-600 rounded-full dark:bg-indigo-900 dark:text-indigo-600 mr-3">
              <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                <path d="M12 4.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z"/>
                <path d="M12 14a1 1 0 01-1-1V8a1 1 0 112 0v5a1 1 0 01-1 1zm-1.5 2.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z"/>
              </svg>
              <span className="sr-only">Check icon</span>
            </div>
            <div className="text-sm font-normal">¿Está seguro de que quiere finalizar?</div>
            <div className="col-span-2">
              <div className="col-span-2 flex justify-end space-x-2 mt-4">
                <button className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300" onClick={(e) => { e.stopPropagation(); setShowFinishMessage(false); }}>Close</button>
                <button className={`btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ${progress === 100 ? '' : 'opacity-50 cursor-not-allowed'}`} disabled={progress < 100} onClick={(e) => { e.stopPropagation(); setShowFinishMessage(false); setAssessmentModalOpen(false); handleFinish(); }}>Finalizar</button>
              </div>
            </div>
          </div>
        </div>
        )}
      </NewAssessmentModal>
      <AssessmentResultsModal id="results-modal" modalOpen={resultsModalOpen} setModalOpen={setResultsModalOpen} title={`Gap Analysis Results - ${props.framework.name}`} isMaximized={isMaximized} toggleMaximize={toggleMaximize}>
        {isLoading && <LoadingSpinner />}
        <div id="results-header" className={`flex grid grid-cols-3 gap-2 items-start dark:bg-slate-900 dark:text-slate-400 px-3 py-3 sticky top-0 ${!isMaximized ? 'w-full' : ''}`} style={{ height: isCollapsed ? '0' : '330px', overflowY: 'hidden', transition: 'height 0.5s ease' }}>
          <div className="flex grid grid-cols-3 items-start dark:text-slate-400 p-2">
            <img src={imageSrc(customerLogo)} width="200" height="240" className="self-star col-span-3"></img>
            <span className="font-bold text-sky-600 pt-6 row-start-2">Progress:</span>
            <div className="justify-center row-start-2 pt-5" style={{ width: '180px' }}> 
              <div style={{ position: 'relative', width: '50%'}}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#FFF',
                  }}
                >
                  {progress}%
                </div>
              </div>
            </div>
            <span className="font-bold text-sky-600 pt-5 row-start-3">Creado por: </span>
            <span className="text-white pt-5 ml-2 row-start-3 col-start-2 col-span-2">{props.owner || ''}</span>
            {props.updatedAt && (
              <>
              <span className="font-bold text-sky-600 pt-2 row-start-4">Actualizado:</span>
              <span className="text-white pt-2 row-start-4 col-start-2 col-span-2 ml-2">{new Date(props.updatedAt).toLocaleString()}</span>
              </>
            )}
            {props.updatedBy && (
              <>
              <span className="font-bold text-sky-600 pt-2 row-start-5">Actualizado por: </span>
              <span className="text-white pt-2 row-start-5 col-start-2 col-span-2 ml-2">{props.updatedBy || ''}</span>
              </>
            )}
          </div>
          <div className="bg-white dark:bg-slate-900 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 px-4">
            <Radar data={radarData} options={radarOptions} />
          </div>
          <div className="flex flex-col justify-center items-center self-start" style={{ width: '340px' }}>
          <label className="text-center mb-2 font-bold text-white py-6">
              Nivel de Cumplimiento General
            </label>
          <GaugeChart id="general-score" colors={["#DE3163", "#FFFF00", "#27AE60"]} arcWidth={0.1} needleScale={0.70} percent={ genScore } />
          </div>
          <button onClick={toggleCollapse} className="absolute bottom-2 right-2 bg-transparent text-gray-800 font-bold px-10 rounded">
            <div className="flex shrink-0 ml-2">
              <svg
                className={`w-4 h-4 shrink-0 ml-1 fill-current text-slate-400 ${!isCollapsed && 'rotate-180'}`}
                viewBox="0 0 12 12"
              >
                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
              </svg>
            </div>
          </button> 
        </div>
        <div className="px-0.5 py-0.5">
          <div id="results-table" className="overflow-x-auto scrollbar-thin scrollbar-track-transparent" style={{ maxHeight: isCollapsed ? '450px' : '250px' }} ref={resultsTableRef}>
            <table className="min-w-full bg-white dark:bg-slate-800 text-xs">
              <thead className="sticky top-0 bg-white dark:bg-slate-800 shadow-lg z-10">
                <tr>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[100px]">Ref</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700">Dominio</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700">Subdominio</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[220px]">Control</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700">Evaluación</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[100px]">Descripción</th>
                  <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 min-w-[480px]">Contramedidas</th>
                  {status === 'submitted' && (
                    <th className="px-4 py-2 border-b-2 border-slate-200 dark:border-slate-700 w-[100px] min-w-[100px]"></th>
                  )}
                </tr>
              </thead>
              <tbody>
              { 
                controls ? (controls.length > 0 ?   
                controls.map(control => {
                  const propCounters = compliance.data[control.ref]?.prop_counter || [];
                  return (
                <>    
                <tr key={ control.id } className={compliance.data[control.ref]?.value === 4 ? "opacity-50 bg-gray-800" : ""}>
                  <td className={`px-4 py-2 border-b border-slate-200 dark:border-slate-700 ${getBGName(control.ref, props.framework.id)}`}>{ control.ref }</td>
                  {/*{ control.domain === 'GOBERNAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-amber-100 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'IDENTIFICAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-sky-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'PROTEGER' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-indigo-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'DETECTAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-orange-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'RESPONDER' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-rose-400 text-black font-bold">{ control.domain }</td>) :
                  control.domain === 'RECUPERAR' ? ( <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-green-400 text-black font-bold">{ control.domain }</td>) :*/}
                  <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-indigo-900">{ control.domain }</td>
                  <td className={`px-4 py-2 border-b border-slate-200 dark:border-slate-700 ${getBGName(control.ref, props.framework.id)}`}>{ control.subdomain !== 'none' ? control.subdomain : ''}</td>
                  <td className={`px-4 py-2 border-b border-slate-200 dark:border-slate-700 ${getBGName(control.ref, props.framework.id)}`} style={{ whiteSpace: 'pre-line' }}>{ control.description !== 'none' ? control.description : ''}</td>
                  { isAssessable(control) ? (
                    <>
                    { compliance.data[control.ref]?.value === 1 ? ( 
                      <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 text-xs font-bold bg-red-700 bg-opacity-75 text-slate-100">No Cumple</td>
                    ) : compliance.data[control.ref]?.value === 2 ? (
                      <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 text-xs font-bold bg-amber-500 bg-opacity-75 text-slate-900">Cumple Parcialmente</td> 
                    ) : (
                      <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 text-xs font-bold bg-green-700 bg-opacity-75 text-slate-100">Cumple</td>
                    )}
                    <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                      {compliance.data[control.ref]?.comment || ''}
                    </td>
                    <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                    {editingCounters[control.ref] ? (
                      <textarea
                      defaultValue={countermeasureTexts[control.ref]}
                      onBlur={(e) => {
                        const newText = e.target.value;
                        setCountermeasureTexts(prev => ({
                          ...prev,
                          [control.ref]: newText
                        }));
                        
                        if (newText !== initialTextRef.current[control.ref]) {
                          const updatedCounter = newText
                            .split('\n')
                            .filter(line => line && line.trim() !== '');
                          
                          setCompliance(prev => ({
                            ...prev,
                            data: {
                              ...prev.data,
                              [control.ref]: {
                                ...prev.data[control.ref],
                                counter: updatedCounter,
                              }
                            }
                          }));
                        }
                        setEditingCounters(prev => ({ ...prev, [control.ref]: false }));
                      }}
                        className="w-full p-2 h-40 text-xs text-gray-300 bg-gray-700 rounded border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 [&>*]:mb-4"
                        style={{ lineHeight: "1.5" }}
                      />
                      ) : (
                        compliance.data[control.ref]?.counter?.length > 0 ? (
                          compliance.data[control.ref].counter.map((
                          item, index) => (
                            item && item.trim() !== '' && (
                              <p key={index} className={`${index > 0 ? "mt-4" : ""} flex items-start`}>
                                <span className="mr-2">•</span>
                                {item}
                              </p>
                            )
                          ))
                        ) : ('')
                      )}
                      { generatingRow === control.id && isGenerating && <p className="mt-2">{genMessage}</p> }
                      { generatingRow === control.id && (
                        <div className="mt-2">
                          {displayedLines.map((line, index) => (
                            <div key={index} className="grid grid-cols-[1fr,40px] gap-2 items-center mx-2">
                              <p className="my-2 text-slate-400">{line}</p>
                              <button 
                                className="btn p-1 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-slate-300" 
                                title="Agregar contramedida"
                                onClick={(e) => { e.stopPropagation(); handleAddCounter(control.ref, line); }}
                              >
                              <svg className="w-3 h-3 fill-current text-white" viewBox="0 0 24 24">
                                <path d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z" />
                              </svg>
                              </button>
                              {/*
                              <button 
                                className="btn p-1 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-slate-300" 
                                title="Eliminar sugerencia"
                                onClick={(e) => { e.stopPropagation(); handleDeleteLine(line); }}
                              >
                              <svg className="w-4 h-4 fill-current text-red-500 hover:text-red-600" viewBox="0 0 24 24">
                                <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.59-13L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z"/>
                              </svg>
                              </button>
                              */}
                            </div>
                          ))}
                          {isAnimating && currentLine && (
                            <div className="grid grid-cols-[1fr,40px,40px] gap-2 items-center mx-2">
                              <p className="my-2 text-slate-400">{currentLine}</p>
                              <div></div>
                            </div>
                          )}
                        </div>
                      )}
                      { status !== 'submitted' && (
                        <div className="flex mt-4">
                          <div className="group relative">
                            <button
                            className="btn text-slate-500 hover:bg-slate-700 pt-2 pb-2.5"
                            onClick={() => {
                              setEditingCounters((prev) => ({ ...prev, [control.ref]: true }));
                              const initialText = compliance.data[control.ref]?.counter
                                ?.filter(line => line && line.trim() !== '')
                                .join('\n') || '';
                              initialTextRef.current[control.ref] = initialText;
                              setCountermeasureTexts((prev) => ({
                                ...prev,
                                [control.ref]: initialText,
                              }));
                            }}
                          >
                            <span className="sr-only">Edit</span>
                            <svg className="w-8 h-10 fill-current pb-2" viewBox="0 -3 32 32">
                              <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
                            </svg>
                          </button>
                          <style jsx>{`
                            .tooltip {
                              transition: opacity 0.15s;
                              transition-delay: 2s;
                              opacity: 0;
                              pointer-events: none;
                            }
                            .group:hover .tooltip {
                              opacity: 1;
                              pointer-events: auto;
                            }
                          `}</style>
                          <div className="tooltip absolute
                            bottom-full mb-2
                            left-1/2 -translate-x-1/2
                            bg-slate-700 text-slate-200 px-3 py-2 rounded-lg text-xs
                            shadow-lg border border-slate-700 w-64 z-50
                            after:content-[''] 
                            after:absolute 
                            after:left-1/2 
                            after:-translate-x-1/2
                            after:top-full 
                            after:border-8 
                            after:border-transparent
                            after:border-t-slate-700"
                          >
                            Agrega o edita contramedidas para cerrar la brecha
                          </div>
                        </div>
                        <div className="group relative">
                          <button className="btn hover:bg-slate-700" aria-controls={`counters-${control.id}`} onClick={(e) => { e.stopPropagation(); setExpandedRows((prev) => ({...prev, [control.id]: !prev[control.id],}));}}>
                            <svg className="w-3 h-10 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                              <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                            </svg>
                          </button>
                          <style jsx>{`
                              .tooltip {
                                transition: opacity 0.15s;
                                transition-delay: 2s;
                                opacity: 0;
                                pointer-events: none;
                              }
                              .group:hover .tooltip {
                                opacity: 1;
                                pointer-events: auto;
                              }
                            `}</style>
                            <div className="tooltip absolute
                              bottom-full mb-2
                              left-1/2 -translate-x-1/2
                              bg-slate-700 text-slate-200 px-3 py-2 rounded-lg text-xs
                              shadow-lg border border-slate-700 w-64 z-50
                              after:content-[''] 
                              after:absolute 
                              after:left-1/2 
                              after:-translate-x-1/2
                              after:top-full 
                              after:border-8 
                              after:border-transparent
                              after:border-t-slate-700"
                            >
                              Selecciona controles agrupados por marco, dominio y subdominio
                            </div>
                        </div>
                        <div className="group relative">
                          <button 
                            className="btn hover:bg-slate-700" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setDisplayedLines([]);
                              setGeneratingRow(control.id);
                              if (propCounters && propCounters.length > 0) {
                                const suggestionTexts = propCounters.map(counter => 
                                  counter.description
                                  //`${counter.description} (${counter.framework} - ${counter.ref})`
                                );
                                setDisplayedLines(prevLines => [...prevLines, ...suggestionTexts]);
                              }
                            }}
                          >
                            <svg className="w-3 h-3 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                              <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4 9H9v3H7V9H4V7h3V4h2v3h3v2z"/>
                            </svg>
                            <div className="flex flex-col ml-2">
                              <span className="xs:block text-xs text-gray-300">Mappings</span>
                              <p className="xs:block text-xs text-indigo-500 mt-1 transition-opacity duration-500 ease-in-out">{`${propCounters.length} controles`}</p>
                            </div>
                          </button>
                          <style jsx>{`
                            .tooltip {
                              transition: opacity 0.15s;
                              transition-delay: 2s;
                              opacity: 0;
                              pointer-events: none;
                            }
                            .group:hover .tooltip {
                              opacity: 1;
                              pointer-events: auto;
                            }
                          `}</style>
                          <div className="tooltip absolute
                            bottom-full mb-2
                            left-1/2 -translate-x-1/2
                            bg-slate-700 text-slate-200 px-3 py-2 rounded-lg text-xs
                            shadow-lg border border-slate-700 w-64 z-50
                            after:content-[''] 
                            after:absolute 
                            after:left-1/2 
                            after:-translate-x-1/2
                            after:top-full 
                            after:border-8 
                            after:border-transparent
                            after:border-t-slate-700"
                          >
                            Selecciona controles de otros marcos que apoyan la adopción de este control
                          </div>
                        </div>
                        <div className="group relative">
                          <button className="btn hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); setDisplayedLines([]); setIsGenerating(true); setGeneratingRow(control.id); genAI(control.description); }}>
                          <svg width="40px" height="40px" viewBox="0 0 512 512" version="1.1">
                              <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                  <g id="icon" fill="#808080" transform="translate(64.000000, 64.000000)">
                                      <path d="M320,64 L320,320 L64,320 L64,64 L320,64 Z M171.749388,128 L146.817842,128 L99.4840387,256 L121.976629,256 L130.913039,230.977 L187.575039,230.977 L196.319607,256 L220.167172,256 L171.749388,128 Z M260.093778,128 L237.691519,128 L237.691519,256 L260.093778,256 L260.093778,128 Z M159.094727,149.47526 L181.409039,213.333 L137.135039,213.333 L159.094727,149.47526 Z M341.333333,256 L384,256 L384,298.666667 L341.333333,298.666667 L341.333333,256 Z M85.3333333,341.333333 L128,341.333333 L128,384 L85.3333333,384 L85.3333333,341.333333 Z M170.666667,341.333333 L213.333333,341.333333 L213.333333,384 L170.666667,384 L170.666667,341.333333 Z M85.3333333,0 L128,0 L128,42.6666667 L85.3333333,42.6666667 L85.3333333,0 Z M256,341.333333 L298.666667,341.333333 L298.666667,384 L256,384 L256,341.333333 Z M170.666667,0 L213.333333,0 L213.333333,42.6666667 L170.666667,42.6666667 L170.666667,0 Z M256,0 L298.666667,0 L298.666667,42.6666667 L256,42.6666667 L256,0 Z M341.333333,170.666667 L384,170.666667 L384,213.333333 L341.333333,213.333333 L341.333333,170.666667 Z M0,256 L42.6666667,256 L42.6666667,298.666667 L0,298.666667 L0,256 Z M341.333333,85.3333333 L384,85.3333333 L384,128 L341.333333,128 L341.333333,85.3333333 Z M0,170.666667 L42.6666667,170.666667 L42.6666667,213.333333 L0,213.333333 L0,170.666667 Z M0,85.3333333 L42.6666667,85.3333333 L42.6666667,128 L0,128 L0,85.3333333 Z" id="Combined-Shape">

                          </path>
                                  </g>
                              </g>
                          </svg>
                          <span className="xs:block text-xs text-gray-300 ml-2">Generar con IA</span>
                          </button>
                          <style jsx>{`
                            .tooltip {
                              transition: opacity 0.15s;
                              transition-delay: 2s;
                              opacity: 0;
                              pointer-events: none;
                            }
                            .group:hover .tooltip {
                              opacity: 1;
                              pointer-events: auto;
                            }
                          `}</style>
                          <div className="tooltip absolute
                            bottom-full mb-2
                            left-1/2 -translate-x-1/2
                            bg-slate-700 text-slate-200 px-3 py-2 rounded-lg text-xs
                            shadow-lg border border-slate-700 w-64 z-50
                            after:content-[''] 
                            after:absolute 
                            after:left-1/2 
                            after:-translate-x-1/2
                            after:top-full 
                            after:border-8 
                            after:border-transparent
                            after:border-t-slate-700"
                          >
                            Usa nuestra IA para generar contramedidas automáticamente
                          </div>
                        </div>
                       </div>
                      )}
                    </td>
                    {status === 'submitted' && (  
                      <td className="px-2 border-b border-slate-700 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        {props.userRole === 'regulator' && !clarificationStatuses[control.ref] && (
                          <div className="relative group">
                            <button 
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-700/30 text-slate-300 hover:bg-indigo-500/20 hover:text-indigo-400 transition-all duration-150"
                              onClick={(e) => { 
                                e.stopPropagation();
                                setSelectedControl(control.ref);
                                setShowClarificationModal(true);
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" className="text-current">
                                <path 
                                  fill="currentColor" 
                                  d="M11.07 12.85c.77-1.39 2.25-2.21 3.11-3.44.91-1.29.4-3.7-2.18-3.7-1.69 0-2.52 1.28-2.87 2.34L6.54 6.96C7.25 4.83 9.18 3 11.99 3c2.35 0 3.96 1.07 4.78 2.41.7 1.15 1.11 3.3.03 4.9-1.2 1.77-2.35 2.31-2.97 3.45-.25.46-.35.76-.35 2.24h-2.89c-.01-.78-.13-2.05.48-3.15zM11 16h3v3h-3z"
                                />
                              </svg>
                              <span>Aclaración</span>
                            </button>
                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 text-slate-200 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg">
                              Solicitar Aclaración
                            </div>
                          </div>
                        )}
                        {props.userRole === 'regulator' && clarificationStatuses[control.ref] && (
                          <div className="relative group">
                            <button 
                              className="relative p-2 rounded-full hover:bg-slate-700/30 transition-colors duration-150"
                              onClick={(e) => { 
                                e.stopPropagation();
                                setSelectedControl(control.ref);
                                setShowClarificationModal(true);
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" className="text-slate-400 group-hover:text-indigo-400 transition-colors duration-150">
                                <path 
                                  fill="currentColor" 
                                  d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                                />
                                <circle cx="12" cy="10" r="1.5" fill="currentColor"/>
                                <circle cx="16" cy="10" r="1.5" fill="currentColor"/>
                                <circle cx="8" cy="10" r="1.5" fill="currentColor"/>
                              </svg>
                              {clarificationStatuses[control.ref]?.status === 'resolved' && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-400 rounded-full">
                                  <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping"></span>
                                </span>
                              )}
                            </button>
                            {clarificationStatuses[control.ref]?.status === 'resolved' && (
                              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 text-slate-200 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg">
                                Aclaración Resuelta
                              </div>
                            )}
                          </div>
                        )}
                        {props.userRole !== 'regulator' && clarificationStatuses[control.ref] && (
                          <div className="relative group w-full flex justify-center">
                            <button 
                              className="relative p-2 rounded-full hover:bg-slate-700/30 transition-colors duration-150"
                              onClick={(e) => { 
                                e.stopPropagation();
                                setSelectedControl(control.ref);
                                setShowClarificationModal(true);
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" className="text-slate-400 group-hover:text-indigo-400 transition-colors duration-150">
                                <path 
                                  fill="currentColor" 
                                  d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                                />
                                <circle cx="12" cy="10" r="1.5" fill="currentColor"/>
                                <circle cx="16" cy="10" r="1.5" fill="currentColor"/>
                                <circle cx="8" cy="10" r="1.5" fill="currentColor"/>
                              </svg>
                              {clarificationStatuses[control.ref]?.status === 'pending' && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-400 rounded-full">
                                  <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping"></span>
                                </span>
                              )}
                            </button>
                            {clarificationStatuses[control.ref]?.status === 'pending' && (
                              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 text-slate-200 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg">
                                Aclaración Pendiente
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    )}  
                  </>
                  ) : (
                    <>
                    <td className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 text-sm font-bold bg-slate-900">
                      {compliance.data[control.ref]?.value === 4 ? "N/A" : `${compliance.data[control.ref]?.value || 0}%`}
                    </td>
                    <td className="bg-slate-900 border-b border-slate-700"></td>
                    <td className="bg-slate-900 border-b border-slate-700"></td>
                    {status === 'submitted' && (
                      <td className="bg-slate-900 border-b border-slate-700 w-[100px] min-w-[100px]"></td>
                    )}
                    </>
                  )}
                </tr>
                <tr id={`counters-${control.id}`} role="region" className={`${!expandedRows[control.id] && 'hidden'} bg-slate-900`} >
                  <td></td>
                  <td></td>
                  <td></td>
                  <td colSpan="4" className="p-0">
                    <div className="flex rounded-lg p-4 max-h-96 overflow-x-auto">
                      {/* Framework Column */}
                      <div className="flex-none w-[200px] border-r border-slate-700 pr-4">
                        {Object.entries(groupByFramework(counters)).map(([framework, domains]) => (
                          <div key={framework} className="group">
                            <div 
                              className={`flex items-center cursor-pointer p-2 rounded-md transition-colors duration-150 
                                ${selectedFramework === framework ? 'bg-slate-700' : 'hover:bg-slate-700'}`}
                              onClick={() => {
                                setSelectedFramework(selectedFramework === framework ? null : framework);
                                setSelectedDomain(null);
                                setSelectedSubdomain(null);
                              }}
                            >
                              <svg className="w-4 h-4 shrink-0 fill-current text-indigo-500 mr-2" viewBox="0 0 16 16">
                                <path d="M6 13l4-5-4-5z"/>
                              </svg>
                              <span className="text-xs font-semibold text-slate-200 group-hover:text-white">
                                {framework}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Domain Column */}
                      {selectedFramework && (
                        <div className="flex-none w-[200px] border-r border-slate-700 px-4">
                          {Object.entries(groupByFramework(counters)[selectedFramework]).map(([domain, subdomains]) => (
                            <div key={`${selectedFramework}.${domain}`} className="group">
                              <div 
                                className={`flex items-center cursor-pointer p-2 rounded-md transition-colors duration-150
                                  ${selectedDomain === domain ? 'bg-slate-700' : 'hover:bg-slate-700'}`}
                                onClick={() => {
                                  setSelectedDomain(selectedDomain === domain ? null : domain);
                                  setSelectedSubdomain(null);
                                }}
                              >
                                <svg className="w-3 h-3 shrink-0 fill-current text-indigo-400 mr-2" viewBox="0 0 16 16">
                                  <path d="M6 13l4-5-4-5z"/>
                                </svg>
                                <span className="text-xs text-slate-300 group-hover:text-white">
                                  {domain}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Subdomain Column */}
                      {selectedFramework && selectedDomain && (
                        <div className="flex-none w-[200px] border-r border-slate-700 px-4">
                          {Object.entries(groupByFramework(counters)[selectedFramework][selectedDomain]).map(([subdomain, controls]) => (
                            <div key={`${selectedFramework}.${selectedDomain}.${subdomain}`} className="group">
                              <div 
                                className={`flex items-center cursor-pointer p-2 rounded-md transition-colors duration-150
                                  ${selectedSubdomain === subdomain ? 'bg-slate-700' : 'hover:bg-slate-700'}`}
                                onClick={() => {
                                  setSelectedSubdomain(selectedSubdomain === subdomain ? null : subdomain);
                                }}
                              >
                                <svg className="w-3 h-3 shrink-0 fill-current text-indigo-300 mr-2" viewBox="0 0 16 16">
                                  <path d="M6 13l4-5-4-5z"/>
                                </svg>
                                <span className="text-xs text-slate-400 group-hover:text-white">
                                  {subdomain !== 'none' ? subdomain : ''}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Controls Column */}
                      {selectedFramework && selectedDomain && selectedSubdomain && (
                        <div className="flex-none w-[300px] px-4">
                          {groupByFramework(counters)[selectedFramework][selectedDomain][selectedSubdomain].map((ctrl) => (
                            <div key={`${selectedFramework}.${selectedDomain}.${selectedSubdomain}.${ctrl.ref}`} className="group">
                              <div 
                                className="flex items-center cursor-pointer p-2 hover:bg-slate-700 rounded-md transition-colors duration-150"
                                onClick={(e) => { e.stopPropagation(); handleAddCounter(control.ref, ctrl.description); }}
                              >
                                <span className="text-xs text-slate-400 hover:text-white">
                                  <span className="font-medium text-indigo-400">{ctrl.ref}</span>
                                  {" - "}
                                  {ctrl.description !== 'none' ? ctrl.description : ''}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                </>
                )
                })
                : <tr><td colSpan={7}><p className="text-center py-4">No controls found</p></td></tr>) : <div><svg className="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                </svg><span className="ml-2">Loading</span></div>
              }
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-5 py-4">
          <div className="flex flex-wrap justify-end space-x-2">
            <button className="btn-sm bg-slate-600 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300" onClick={(e) => { e.stopPropagation(); setResultsModalOpen(false); }}>Close</button>
            {status !== 'submitted' && props.canUpdate && (
            <button className={`btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ${saveEnabled ? '' : 'opacity-50 cursor-not-allowed'}`} 
              disabled={!saveEnabled} 
              onClick={(e) => { e.stopPropagation(); handleSave(); }}>
              Guardar
            </button>
            )}
            {(status === 'finished' || status === 'submitted') && props.canUpdate && (
              <button 
                className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setShowReEvalModal(true);
                }}>
                Re-evaluar
              </button>
            )}
            {status === 'finished' && props.userRole !== 'regulator' && (
              <button 
                className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
                onClick={(e) => { e.stopPropagation(); setShowPresentMessage(true); }}>
                Presentar
              </button>
            )}
          </div>
        </div>
        {showSaveMessage && (
        <div id="save-success" className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out">
          <div className="flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-700" role="alert">
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-green-500 bg-green-100 rounded-lg dark:bg-green-800 dark:text-green-200">
              <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
              </svg>
              <span className="sr-only">Check icon</span>
            </div>
            <div className="ms-3 text-sm text-slate-300">Datos guardados con éxito</div>
          </div>
        </div>
        )}
        {showPresentMessage && (
          <div id="confirm-present" className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 border-slate-300 ease-in-out">
            <div className="grid grid-cols-2 items-center w-full max-w-sm p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-700" role="alert" style={{ gridTemplateColumns: '0.5fr 3fr' }}>
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-indigo-600 bg-indigo-600 rounded-full dark:bg-indigo-900 dark:text-indigo-600 mr-4">
              <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                <path d="M12 4.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z"/>
                <path d="M12 14a1 1 0 01-1-1V8a1 1 0 112 0v5a1 1 0 01-1 1zm-1.5 2.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z"/>
              </svg>
              <span className="sr-only">Check icon</span>
            </div>
            <div className="text-sm text-slate-300">¿Presentar al regulador?</div>
            <div className="col-span-2">
              <div className="col-span-2 flex justify-end space-x-2 mt-4">
                <button className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300" onClick={(e) => { e.stopPropagation(); setShowPresentMessage(false); }}>Close</button>
                <button className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white" onClick={(e) => { e.stopPropagation(); setShowPresentMessage(false); updateEngagementStatus('submitted'); }}>Presentar</button>
              </div>
            </div>
            </div>
          </div>
        )}
        {showInvalidFileModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 border-slate-300 ease-in-out">
            <div className="grid grid-cols-2 items-center w-full max-w-sm p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-700" role="alert" style={{ gridTemplateColumns: '0.5fr 3fr' }}>
              <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-indigo-600 bg-indigo-600 rounded-full dark:bg-indigo-900 dark:text-indigo-600 mr-4">
                <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                  <path d="M12 4.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z"/>
                  <path d="M12 14a1 1 0 01-1-1V8a1 1 0 112 0v5a1 1 0 01-1 1zm-1.5 2.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z"/>
                </svg>
                <span className="sr-only">Warning icon</span>
              </div>
              <div className="text-sm text-slate-300">
                <p>Algunos archivos no están permitidos:</p>
                <ul className="mt-2 list-disc list-inside">
                  {invalidFiles.map((fileName, index) => (
                    <li key={index} className="text-xs">{fileName}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs">Solo se permiten archivos de documento (.pdf, .docx, .xlsx, .csv, etc.)</p>
              </div>
              <div className="col-span-2">
                <div className="col-span-2 flex justify-end space-x-2 mt-4">
                  <button 
                    className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInvalidFileModal(false);
                      setInvalidFiles([]);
                    }}
                  >
                    Entendido
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AssessmentResultsModal>
      {showReEvalModal && (
        <div id="confirm-reeval" className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out">
          <div className="grid grid-cols-2 items-center w-full max-w-sm p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-700" role="alert" style={{ gridTemplateColumns: '0.5fr 3fr' }}>
            <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-indigo-600 bg-indigo-600 rounded-full dark:bg-indigo-900 dark:text-indigo-600 mr-4">
              <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                <path d="M12 4.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z"/>
                <path d="M12 14a1 1 0 01-1-1V8a1 1 0 112 0v5a1 1 0 01-1 1zm-1.5 2.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z"/>
              </svg>
            </div>
            <div className="text-sm text-slate-300">
              ¿Iniciar re-evaluación?
            </div>
            <div className="col-span-2">
              <div className="col-span-2 flex justify-end space-x-2 mt-4">
                <button 
                  className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300" 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setShowReEvalModal(false); 
                  }}>
                  Cancelar
                </button>
                <button 
                  className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white" 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setShowReEvalModal(false);
                    updateEngagementStatus('in progress');
                    setResultsModalOpen(false);
                    setAssessmentModalOpen(true);
                  }}>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showClarificationModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-slate-900 rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-y-auto border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700 bg-slate-800">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-slate-200 tracking-wider uppercase text-xs">
                  {!clarificationStatuses[selectedControl]
                    ? 'Solicitar Aclaración'
                    : clarificationStatuses[selectedControl]?.status === 'finalized'
                      ? 'Aclaración Finalizada'
                      : clarificationStatuses[selectedControl]?.status === 'resolved'
                        ? 'Aclaración Resuelta'
                        : 'Aclaración Solicitada'}
                </div>
                <button 
                  className="text-slate-400 hover:text-slate-200 transition-colors duration-150"
                  onClick={() => {
                    setShowClarificationModal(false);
                    setSelectedControl(null);
                    setClarificationComment('');
                  }}
                >
                  <svg className="w-4 h-4 fill-current">
                    <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="px-6 py-5">
              {!clarificationStatuses[selectedControl] ? (
                // Show request form if there's no existing clarification
                <>
                  <div className="bg-slate-800 rounded-lg p-5 mb-4">
                    <div className="mb-4">
                      <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                        Comentario
                      </label>
                      <textarea
                        className="form-textarea w-full bg-slate-700 border-0 focus:ring-2 focus:ring-slate-500 text-slate-200 rounded-md shadow-inner"
                        rows="3"
                        value={clarificationComment}
                        onChange={(e) => setClarificationComment(e.target.value)}
                        placeholder="Escriba su solicitud de aclaración..."
                      />
                    </div>
                    <div className="flex flex-wrap justify-end space-x-2">
                      <button
                        className="px-4 py-2 rounded-md text-sm font-medium text-slate-400 hover:text-slate-200 transition-colors duration-150"
                        onClick={() => {
                          setShowClarificationModal(false);
                          setSelectedControl(null);
                          setClarificationComment('');
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white shadow-sm transition-colors duration-150"
                        onClick={handleClarificationRequest}
                        disabled={!clarificationComment.trim()}
                      >
                        Enviar
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Show clarification history
                <>
                  <div className="space-y-6">
                    <div className="grid grid-cols-[0.1fr_2fr] gap-2">
                      <div className="relative flex flex-col items-center">
                        {/* Vertical line */}
                        <div className="absolute h-full w-0.5 bg-slate-600"></div>
                        
                        {/* Progress dots - using absolute positioning to align with boxes */}
                        <div className="relative z-10 h-full">
                          {/* Request dot - always shown and active */}
                          <div className="absolute top-7 -left-[8px] w-4 h-4 rounded-full bg-indigo-500 ring-4 ring-slate-800"></div>
                          
                          {/* Response dot - shown if pending response or has response */}
                          {(clarificationStatuses[selectedControl]?.resolution_comment || 
                            (props.userRole !== 'regulator' && 
                             clarificationStatuses[selectedControl]?.status === 'pending')) && (
                            <div className={`absolute top-[16rem] -left-[8px] w-4 h-4 rounded-full ${
                              clarificationStatuses[selectedControl]?.resolution_comment 
                                ? 'bg-indigo-500' 
                                : 'bg-slate-600'
                            } ring-4 ring-slate-800`}></div>
                          )}
                          
                          {/* Finalization dot - only shown when regulator can finalize or when already finalized */}
                          {(clarificationStatuses[selectedControl]?.status === 'finalized' || 
                            (clarificationStatuses[selectedControl]?.status === 'resolved' && 
                             props.userRole === 'regulator')) && (
                            <div className={`absolute top-[28rem] -left-[8px] w-4 h-4 rounded-full ${
                              clarificationStatuses[selectedControl]?.status === 'finalized'
                                ? 'bg-indigo-500'
                                : 'bg-slate-600'
                            } ring-4 ring-slate-800`}></div>
                          )}
                        </div>
                      </div>
                      <div>
                        {/* Request Box */}
                        <div className="bg-slate-800 rounded-lg p-5 mb-4">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                Solicitado por
                              </label>
                              <div className="text-slate-200">
                                {clarificationStatuses[selectedControl]?.requester_name}
                              </div>
                            </div>
                            <div>
                              <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                Fecha de solicitud
                              </label>
                              <div className="text-slate-200">
                                {new Date(clarificationStatuses[selectedControl]?.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                              Solicitud de aclaración
                            </label>
                            <div className="text-slate-200 bg-slate-700 p-4 rounded-md shadow-inner">
                              {clarificationStatuses[selectedControl]?.comment}
                            </div>
                          </div>
                        </div>
                      
                        {/* Response Box - Show only if there's a resolution or it's pending user response */}
                        {(clarificationStatuses[selectedControl]?.resolution_comment || 
                          (props.userRole !== 'regulator' && 
                          clarificationStatuses[selectedControl]?.status === 'pending')) && (
                          <div className="bg-slate-800 rounded-lg p-5 mb-4">
                            {clarificationStatuses[selectedControl]?.resolution_comment && (
                              <>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                      Resuelto por
                                    </label>
                                    <div className="text-slate-200">
                                      {clarificationStatuses[selectedControl]?.resolver_name}
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                      Fecha de resolución
                                    </label>
                                    <div className="text-slate-200">
                                      {new Date(clarificationStatuses[selectedControl]?.resolved_at).toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                    Respuesta
                                  </label>
                                  <div className="text-slate-200 bg-slate-700 p-4 rounded-md shadow-inner">
                                    {clarificationStatuses[selectedControl]?.resolution_comment}
                                  </div>
                                </div>
                              </>
                            )}
                            
                            {/* Show response textarea only for users when clarification is pending */}
                            {!clarificationStatuses[selectedControl]?.resolution_comment && 
                            props.userRole !== 'regulator' && 
                            clarificationStatuses[selectedControl]?.status !== 'finalized' && (
                              <div>
                                <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                  Respuesta
                                </label>
                                <textarea
                                  className="form-textarea w-full bg-slate-700 border-0 focus:ring-2 focus:ring-slate-500 text-slate-200 rounded-md shadow-inner"
                                  rows="3"
                                  value={clarificationComment}
                                  onChange={(e) => setClarificationComment(e.target.value)}
                                  placeholder="Escriba su respuesta a la solicitud de aclaración..."
                                />
                                <p className="mt-2 text-xs text-slate-500">
                                  Si necesita adjuntar archivos de soporte, puede agregarlos en la sección de evidencias.
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Finalization Box - Show for finalized clarifications or when regulator can finalize */}
                        {(clarificationStatuses[selectedControl]?.status === 'finalized' || 
                          (clarificationStatuses[selectedControl]?.status === 'resolved' && 
                          props.userRole === 'regulator')) && (
                          <div className="bg-slate-800 rounded-lg p-5">
                            {clarificationStatuses[selectedControl]?.status === 'finalized' && (
                              <>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                      Finalizado por
                                    </label>
                                    <div className="text-slate-200">
                                      {clarificationStatuses[selectedControl]?.finalizer_name}
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                      Fecha de finalización
                                    </label>
                                    <div className="text-slate-200">
                                      {new Date(clarificationStatuses[selectedControl]?.finalized_at).toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                                {clarificationStatuses[selectedControl]?.final_comment && (
                                  <div>
                                    <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                      Comentario Final
                                    </label>
                                    <div className="text-slate-200 bg-slate-700 p-4 rounded-md shadow-inner">
                                      {clarificationStatuses[selectedControl]?.final_comment}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Show finalization textarea for regulators when status is resolved */}
                            {clarificationStatuses[selectedControl]?.status === 'resolved' && 
                            props.userRole === 'regulator' && (
                              <div>
                                <label className="block uppercase tracking-wide text-xs font-medium text-slate-400 mb-2">
                                  Comentario Final (Opcional)
                                </label>
                                <textarea
                                  className="form-textarea w-full bg-slate-700 border-0 focus:ring-2 focus:ring-slate-500 text-slate-200 rounded-md shadow-inner"
                                  rows="3"
                                  value={clarificationComment}
                                  onChange={(e) => setClarificationComment(e.target.value)}
                                  placeholder="Agregar un comentario final..."
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      className="px-4 py-2 rounded-md text-sm font-medium text-slate-300 hover:text-white border border-slate-600 bg-slate-900 hover:bg-slate-800 transition-colors duration-150 shadow-sm"
                      onClick={() => {
                        setShowClarificationModal(false);
                        setSelectedControl(null);
                        setClarificationComment('');
                      }}
                    >
                      Cerrar
                    </button>
                    {/* Show submit button only for users when clarification is pending */}
                    {!clarificationStatuses[selectedControl]?.resolution_comment && 
                     props.userRole !== 'regulator' && 
                     clarificationStatuses[selectedControl]?.status !== 'finalized' && (
                      <button
                        className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white shadow-sm transition-colors duration-150"
                        onClick={handleClarificationResponse}
                        disabled={!clarificationComment.trim()}
                      >
                        Enviar
                      </button>
                    )}
                    {/* Show finalize button for regulators when status is resolved */}
                    {clarificationStatuses[selectedControl]?.status === 'resolved' && 
                     props.userRole === 'regulator' && (
                      <button
                        className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white shadow-sm transition-colors duration-150"
                        onClick={handleFinalizeClarification}
                      >
                        Finalizar
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {notificationOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out">
          <div className="bg-slate-800 rounded shadow-lg overflow-auto max-w-lg w-full max-h-full" onClick={e => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex justify-between items-center mb-2">
                <div className="text-slate-200">
                  <div className={`inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg ${
                    notificationType === 'success' 
                      ? 'text-green-500 bg-green-100 dark:bg-green-800 dark:text-green-200'
                      : 'text-red-500 bg-red-100 dark:bg-red-800 dark:text-red-200'
                  }`}>
                    {notificationType === 'success' ? (
                      <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/>
                      </svg>
                    )}
                  </div>
                </div>
                <button 
                  type="button" 
                  className="text-slate-400 hover:text-slate-200"
                  onClick={() => setNotificationOpen(false)}
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M15.898,4.045c-0.271-0.272-0.713-0.272-0.986,0l-4.71,4.711L5.493,4.045c-0.272-0.272-0.714-0.272-0.986,0s-0.272,0.714,0,0.986l4.709,4.711l-4.71,4.711c-0.272,0.271-0.272,0.713,0,0.986c0.136,0.136,0.314,0.203,0.492,0.203c0.179,0,0.357-0.067,0.493-0.203l4.711-4.711l4.71,4.711c0.137,0.136,0.314,0.203,0.494,0.203c0.178,0,0.355-0.067,0.492-0.203c0.273-0.273,0.273-0.715,0-0.986l-4.711-4.711l4.711-4.711C16.172,4.759,16.172,4.317,15.898,4.045z"></path>
                  </svg>
                </button>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-slate-200">
                  {notificationType === 'success' ? 'Operación Exitosa' : 'Error'}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-slate-300">
                    {notificationMessage}
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                    notificationType === 'success' 
                      ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                  onClick={() => setNotificationOpen(false)}
                >
                  Aceptar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Use React.memo with a custom comparison function for props
export default memo(EngagementsTableItem, (prevProps, nextProps) => {
  // Only re-render if these specific props changed
  return (
    prevProps.id === nextProps.id &&
    prevProps.isChecked === nextProps.isChecked &&
    prevProps.status === nextProps.status &&
    prevProps.customer === nextProps.customer
    // Add other props that should trigger re-renders when changed
  );
});
